import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, X, Sparkles, ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { Badge, Button, Card, cn } from './ui';
import type { OnboardingState } from '@shared/types';

/**
 * The setup guide, shown on the dashboard until it is finished or put away.
 *
 * ── Why a checklist and not a wizard ──────────────────────────────────────
 *
 * A wizard decides the order and will not let you past step three. Landlords
 * arrive mid-stream — half the properties already let, one tenant moving in on
 * Friday, last year's arrears to record — and being told to finish step three
 * first is how software gets abandoned in the first ten minutes.
 *
 * So: a list of links, in the order the work usually happens, each saying why
 * it matters rather than just what to press. Every step is checked against
 * real data, so it cannot claim something is done when it is not, and nothing
 * is blocked by anything else.
 */
export function GettingStarted() {
  const { data, reload } = useApi<OnboardingState>('/onboarding');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Nothing to show once it is finished or dismissed — and nothing while it
  // loads either, because a panel that appears a beat late shifts the whole
  // dashboard under the cursor.
  if (!data || data.dismissed || data.complete) return null;

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post('/onboarding', { dismissed: true });
      reload();
    } finally {
      setBusy(false);
    }
  };

  const tick = async (key: string) => {
    setBusy(true);
    try {
      await api.post('/onboarding', { step: key, done: true });
      reload();
    } finally {
      setBusy(false);
    }
  };

  const percent = Math.round((data.completed / data.total) * 100);

  return (
    <Card className="p-0 overflow-hidden">
      <div className="flex items-start gap-3 p-4 sm:p-5 border-b border-line">
        <div className="shrink-0 h-9 w-9 rounded-xl bg-brand/12 flex items-center justify-center">
          <Sparkles className="h-[18px] w-[18px] text-brand" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold leading-tight">Getting set up</h2>
          <p className="text-sm text-muted mt-0.5">
            {data.completed} of {data.total} done.
            {data.is_demo
              ? ' This is a demo account — try anything you like, none of it is real.'
              : data.trial_days_left !== null
                ? ` ${data.trial_days_left} days left on your trial.`
                : ''}
          </p>
        </div>
        <button
          onClick={dismiss}
          disabled={busy}
          className="shrink-0 p-1.5 rounded-lg text-faint hover:text-ink hover:bg-raised transition-colors"
          aria-label="Hide the setup guide"
          title="Hide this. You can reopen it from Help."
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="h-1 bg-raised">
        <div
          className="h-full bg-brand transition-[width] duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      <ul className="divide-y divide-line">
        {data.steps.map((step) => {
          const open = expanded === step.key;
          return (
            <li key={step.key}>
              <div className="flex items-center gap-3 px-4 sm:px-5 py-3">
                <span
                  className={cn(
                    'shrink-0 h-5 w-5 rounded-full border flex items-center justify-center',
                    step.done
                      ? 'bg-ok/15 border-ok/40 text-ok'
                      : 'border-line text-transparent',
                  )}
                  aria-hidden
                >
                  <Check className="h-3 w-3" />
                </span>

                <button
                  onClick={() => setExpanded(open ? null : step.key)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className={cn('text-sm font-medium', step.done && 'text-muted line-through')}>
                    {step.title}
                  </span>
                  {!step.required && !step.done && (
                    <Badge tone="neutral" className="ml-2">Optional</Badge>
                  )}
                  {typeof step.count === 'number' && step.count > 0 && !step.done && (
                    <Badge tone="warn" className="ml-2">{step.count}</Badge>
                  )}
                </button>

                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 text-faint transition-transform', open && 'rotate-180')}
                  aria-hidden
                />
              </div>

              {open && (
                <div className="px-4 sm:px-5 pb-4 pl-12 space-y-3">
                  <p className="text-sm text-muted leading-relaxed">{step.why}</p>
                  <div className="flex flex-wrap gap-2">
                    <Link to={step.path}>
                      <Button size="sm" variant="primary" icon={<ArrowRight className="h-3.5 w-3.5" />}>
                        {step.action}
                      </Button>
                    </Link>
                    {/*
                      Only the informational steps can be ticked by hand. The
                      rest are read from the data, so a button here would be
                      offering to tell the user a lie.
                    */}
                    {step.manual && !step.done && (
                      <Button size="sm" onClick={() => tick(step.key)} loading={busy}>
                        Mark as done
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
